import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import React, {useEffect, useState} from 'react';
import {Grid, IconButton} from '@mui/material';
import moment from 'moment/moment';
import config from '../../../app/config';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import useLongPress from '../../../hooks/useLongPress';
import {agylePref, saveAgylePreference} from '../../../store/reducers/preferences';
import _ from 'lodash';
import {setSnackbar} from '../../../store/reducers/globals';
import {useHistory} from 'react-router-dom';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import clsx from 'clsx';
import Tooltip from '@mui/material/Tooltip';
import {useTheme} from '@mui/material/styles';
import {setLastVisitMap} from '../../../store/reducers/wardMap';
import WardCardThumbnail from './wardCardThumbnail';

const useWardCardStyl = makeStyles((theme) => ({
    root: {
        width: '95%',
    },
    map: {
        border: '2px solid grey',
        borderRadius: 15,
        padding: 10,
        cursor: 'pointer',
        maxHeight: 280,
        height: 280,
        position: 'relative',
        width: '100%',
        '&.selected': {
            borderColor: theme.palette.primary.main,
        },
    },
    lastEdit: {
        width: '92%',
        bottom: 0,
        position: 'absolute',
        backgroundColor: theme.palette.background.paper,
        margin: '15px 0',
        borderRadius: 5,
        padding: 5,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        zIndex: 8,
        right: 10,
    },
    footer: {
        margin: 5,
    },
    mapName: {
        fontWeight: 'bold',
        color: theme.palette.inPatient.text,
    },
    mapDetails: {
        fontSize: 'small',
        color: 'grey',
    },
    editDetails: {
        color: 'grey',
        marginLeft: 5,
    },
    image: {
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        height: '100%',
        width: '100%',
        color: theme.palette.mode === 'light' ? '#a4a4a4' : '#eeeeee',
    },
    favorite: {
        '&:hover': {
            color: theme.palette.mode === 'light' ? 'black' : 'grey',
        },
    },
    live: {
        position: 'absolute',
        top: 10,
        left: 10,
        backgroundColor: '#FF4444',
        padding: '5px 15px',
        borderRadius: 5,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        color: '#ffffff',
    },
    liveIcon: {
        backgroundColor: '#FF0000',
        border: '1px solid #FFF',
        borderRadius: '50%',
        width: 12,
        height: 12,
        marginRight: 5,
        color: '#ffffff',
    },
    combined: {
        position: 'absolute',
        top: 50,
        left: 10,
        backgroundColor: theme.palette.mode === 'light' ? '#DEE1E5' : '#3d3e40',
        padding: '5px 10px',
        borderRadius: 5,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        color: theme.palette.mode === 'light' ? '#000' : '#ffffff',
        fontSize: 'small',
    },
    wardsId: {
        position: 'absolute',
        bottom: 50,
        right: 10,
        backgroundColor: theme.palette.mode === 'light' ? '#DEE1E5' : '#3d3e40',
        padding: '5px 10px',
        borderRadius: 5,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        color: theme.palette.mode === 'light' ? '#000' : '#ffffff',
        fontSize: 10,
        zIndex: 9,
    },
    select: {
        position: 'absolute',
        top: 10,
        right: 10,
        '&.selected': {
            color: theme.palette.primary.main,
        },
    },
}));

const longPressOptions = {
    shouldPreventDefault: true,
    delay: 500,
};

const WardCard = ({ward, creatorMode, multiSelectEnabled, setMultiSelectEnabled, handleSelectedMap}) => {
    const classes = useWardCardStyl();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const agylePreference = useSelector(agylePref);
    const theme = useTheme();

    const [isFavorite, setIsFavorite] = useState(false);

    // Select
    const [isSelected, setIsSelected] = useState(false);

    const longPressEvent = useLongPress(onLongPress, onClick, longPressOptions);

    useEffect(() => {
        if (!multiSelectEnabled) setIsSelected(false);
    }, [multiSelectEnabled]);

    // Check if a map is in favorite
    useEffect(() => {
        if (!_.isEmpty(agylePreference?.wardMap?.favorites)) {
            setIsFavorite(agylePreference?.wardMap?.favorites?.includes(ward.__id));
        }
    }, [agylePreference]);

    const toggleFavorite = () => {
        let clone = agylePreference?.wardMap?.favorites ? _.cloneDeep(agylePreference?.wardMap?.favorites) : [];
        if (isFavorite) {
            clone = _.filter(clone, id => id !== ward.__id);
            dispatch(setSnackbar({type: 'success', message: t('The map has been removed from favorites.')}));
            setIsFavorite(false);
        } else {
            clone.push(ward.__id);
            dispatch(setSnackbar({type: 'success', message: t('The map was added to favorites.')}));
            setIsFavorite(true);
        }

        dispatch(saveAgylePreference({
            wardMap: {
                ...agylePreference.wardMap,
                favorites: clone,
            },
        }));
    };

    const isEdited = () => {
        return (ward._createdBy !== ward._lastModifiedBy) || (ward._createdDate !== ward._lastModifiedDate);
    };

    const getFooter = () => {
        let prefix;
        let date;
        let user;
        if (ward?.active) {
            prefix = 'Live';
            date = ward.activeDate;
            user = ward.activeBy;
        } else if (ward?.published) {
            prefix = 'Published';
            date = ward.publishedDate;
            user = ward.publishedBy;
        } else {
            prefix = 'Drafted';
            date = ward._createdDate;
            user = ward._createdBy;
        }
        return `${t(prefix)} ${moment(date).format(config.defaultDateFormat)} by ${user}`;
    };

    function onLongPress() {
        if (!multiSelectEnabled && creatorMode) {
            setMultiSelectEnabled(true);
        }
    }

    function onClick() {
        if (multiSelectEnabled) {
            const bool = isSelected;
            setIsSelected(!bool);
            handleSelectedMap(ward.__id, !bool);
        } else if (ward.code) {
            if (ward.active) {
                dispatch(setLastVisitMap(ward?.__id));
                history.push(`/wardNew/${ward.code}`);
            } else {
                dispatch(setSnackbar({type: 'warning', message: t('The map is not active. Please contact support.')}));
            }
        } else {
            dispatch(setSnackbar({type: 'error', message: t('The map has no code. Please contact support.')}));
        }
    }

    const getWardThumbnail = () => {
        const mainView = _.find(ward?.views, {main: true});

        if (!_.isEmpty(mainView?.areas)) {
            return <WardCardThumbnail elements={mainView.areas} width={Number(ward.width)} height={Number(ward.height)}/>
        } else {
            return <ImageNotSupportedIcon className={classes.image} style={{width: '100%', height: '100%'}}/>
        }
    };

    const renderWardIds = () => {
        let wards = ward?.wardsId || [];
        let color;
        if (wards?.length > 3) {
            wards = _.slice(wards, 0, 3);
            wards.push('...');
        }
        const ids = wards.join(', ');

        if (wards[0] === 'ED') {
            color = theme.palette.attendanceType.ed;
        } else {
            color = theme.palette.attendanceType.ip;
        }

        if (_.isEmpty(wards)) return null;
        return (
            <Tooltip title={ward?.wardsId?.join(', ') || ''}>
                <div className={classes.wardsId} style={{backgroundColor: color}}>
                    {ids}
                </div>
            </Tooltip>
        );
    };

    return (
        <div className={classes.root}>
            <div className={clsx(classes.map, isSelected && 'selected')} {...longPressEvent}>
                {ward.active &&
                    <div className={classes.live}>
                        <div className={classes.liveIcon}/>
                        {t('LIVE')}
                    </div>
                }

                {renderWardIds()}

                {ward?.wardsId?.length > 1 &&
                    <div className={classes.combined}>
                        {t('Combined')}
                    </div>
                }

                {multiSelectEnabled &&
                    <div className={clsx(classes.select, isSelected && 'selected')}>
                        {isSelected
                            ? <CheckCircleIcon/>
                            : <RadioButtonUncheckedIcon/>
                        }
                    </div>
                }

                {getWardThumbnail()}

                {isEdited() &&
                    <div className={classes.lastEdit}>
                        <BorderColorOutlinedIcon fontSize="small" style={{color: 'grey'}}/>
                        <span className={classes.editDetails}>
                            {`${t('Edited')} ${moment(ward._lastModifiedDate).format(config.defaultDateTimeFormat)} by ${ward._lastModifiedBy}`}
                        </span>
                    </div>
                }
            </div>
            <Grid container direction="row" justifyContent="space-between" className={classes.footer}>
                <div>
                    <div className={classes.mapName}>
                        {ward.name || 'Unknown'}
                    </div>
                    <div className={classes.mapDetails}>
                        {getFooter()}
                    </div>
                </div>
                <IconButton
                    onClick={toggleFavorite}
                    disableRipple
                    className={classes.favorite}
                    disabled={!ward.active && !isFavorite}
                >
                    {isFavorite ? <StarIcon/> : <StarBorderIcon/>}
                </IconButton>
            </Grid>
        </div>
    );
};

export default WardCard;